import { css, cx } from "@emotion/css";
import dayjs from "dayjs";
import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { IconTextField } from "src/components/Default/IconTextField";
import {
	IconButton,
	TextButton,
	TextPrimaryButton,
} from "src/components/Default/LekUI/Button";
import { SwipeableContent } from "src/components/Default/LekUI/SwipableDrawer";
import { Tooltip } from "src/components/Default/LekUI/Tooltip";
import { EllipsisText } from "src/components/Main/News/EllipsisText";
import { AddQuestionModal } from "src/components/Main/News/Forum/AddQuestionModal";
import { ForumCommentContent } from "src/components/Main/News/Forum/ForumCommentContent";
import {
	ActionContainerId,
	SearchContainerId,
} from "src/components/Main/News/News";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import Gallery from "src/components/NewComponents/Images/Gallery";
import { ShortLimitedAccess } from "src/components/NewComponents/LimitedAccess";
import ExamsShortViewSkeleton from "src/components/NewComponents/Skeletons/ExamsShortView";
import { hasLimitedAccess } from "src/helpers/getPermissions";
import isMobile from "src/helpers/isMobile";
import {
	useGetForumCommentSubcomments,
	useGetForumComments,
	useRemoveForumComment,
} from "src/hooks/useComments";
import { useFontSize } from "src/hooks/useFontSize";
import { getUserId, isStaff } from "src/hooks/useUser";
import ArrowDownIcon from "src/images/Icons/newIcons/arrowDown.svg";
import ArrowUpIcon from "src/images/Icons/newIcons/arrowUp.svg";
import DeleteIcon from "src/images/Icons/newIcons/delete.svg";
import PlusIcon from "src/images/Icons/newIcons/plus.svg";
import AdminProfileIcon from "src/images/Icons/newIcons/userProfile/logo.svg";
import ProfileIcon from "src/images/Icons/newIcons/userProfile/profile.svg";

interface ForumComment {
	id: string;
	comment: string;
	date: string;
	subcommentsCount: number;
	images?: { url: string }[];
	user: {
		id: string;
		username: string;
		isStaff: boolean;
	};
}

interface ForumComments {
	subComments: ForumComment[];
}

interface UserQuestionProps {
	comment: ForumComment;
	subcomments?: ForumComments | null;
}

const UserQuestion = ({ comment, subcomments = null }: UserQuestionProps) => {
	const { openModal } = useModals();
	const { mutateAsync: removeForumComment } =
		useRemoveForumComment() as unknown as {
			mutateAsync: (payload: { id: string | number }) => Promise<void>;
		};
	const [isOpen, setIsOpen] = useState(false);
	const { fontSizeClass } = useFontSize();
	const Logo = comment.user.isStaff ? AdminProfileIcon : ProfileIcon;
	const { t } = useTranslation();
	const Icon = isOpen ? ArrowUpIcon : ArrowDownIcon;
	const { data: forumComments } = useGetForumCommentSubcomments(
		comment.id,
		subcomments ? true : isOpen,
	);

	const renderSeeMore = () => {
		const count = forumComments?.subComments
			? forumComments?.subComments.length
			: comment.subcommentsCount;

		if (count === 0) {
			return t("news.forum.addAnswer");
		}

		return `${t("news.forum.seeAnswers")} (${count})`;
	};

	const renderRemoveComment = () => {
		if (!isStaff() && getUserId() !== comment.user.id) {
			return null;
		}

		return (
			<div>
				<Tooltip title={t("tooltips.removeQuestion")}>
					<IconButton
						aria-label="remove comment"
						size="small"
						onClick={() => {
							openModal(ModalsNames.DeleteItemModal, {
								title: t("modals.deleteItem.comment.title", "Usunąć komentarz?"),
								description: t(
									"modals.deleteItem.comment.description",
									"Komentarz zostanie trwale usunięty. Tej operacji nie można cofnąć.",
								),
								confirmText: t("modals.deleteItem.comment.confirm", "Usuń komentarz"),
								onDelete: () => removeForumComment({ id: comment.id }),
							});
						}}
					>
						<DeleteIcon className="icon-small icon-error" />
					</IconButton>
				</Tooltip>
			</div>
		);
	};

	return (
		<div className="content-box flex-col last_comment of-auto">
			<div className="flex gap-16">
				<Tooltip
					title={comment.user.isStaff ? t("tooltips.admin") : ""}
					className="h-fit-content"
				>
					<div className="profile--user-avatar square-48">
						<Logo className="icon-normal" />
					</div>
				</Tooltip>
				<div className="flex-1">
					<p className={`m-0 ${fontSizeClass}`}>
						<b>{comment.user.username}</b> {t("news.forum.userComment")}{" "}
					</p>
					<span className="text-grey">
						{dayjs(comment.date).format("DD MMM, HH:mm")}
					</span>
				</div>
				{renderRemoveComment()}
			</div>
			<div className="flex mt-12 gap-16 flex-col">
				<div className="flex-1 flex-col gap-12 flex">
					<EllipsisText
						fontSizeClassName={fontSizeClass}
						className={`${fontSizeClass} fr-view`}
						html={comment.comment}
					/>
					{comment?.images?.[0]?.url && (
						<div>
							<Gallery
								images={[{ image: comment.images[0].url }]}
								maxImages={isMobile() ? 5 : null}
								onDelete={undefined}
							/>
						</div>
					)}
				</div>
				<div>
					<TextPrimaryButton
						onClick={() => setIsOpen(!isOpen)}
						className={cx(
							"p-2",
							css`
                height: unset;
              `,
						)}
						endIcon={<Icon className="icon-normal" />}
					>
						{renderSeeMore()}
					</TextPrimaryButton>
				</div>
				{isOpen && forumComments && (
					<SwipeableContent isOpen={isOpen} onClose={() => setIsOpen(false)}>
						<div className="p-12">
							<ForumCommentContent
								comments={forumComments.subComments}
								isActive
								parentComment={comment}
								placeholder={t("exam.comments.replyComment")}
							/>
						</div>
					</SwipeableContent>
				)}
			</div>
		</div>
	);
};

interface QuestionsAndAnswersProps {
	isOpen: boolean;
}

export function QuestionsAndAnswers({ isOpen }: QuestionsAndAnswersProps) {
	const [search, setSearch] = useState("");
	const [isModalOpen, setIsModalOpen] = useState(false);
	const actionContainer = document.getElementById(ActionContainerId);
	const searchContainer = document.getElementById(SearchContainerId);
	const [isFirstFetch, setFirstFetch] = useState(true);
	const { data, hasNextPage, fetchNextPage, isFetching, isFetched } =
		useGetForumComments({ search }, isOpen);
	const fetchedPages = (data as any)?.currentPage ?? 0;

	const { t } = useTranslation();

	const renderContent = () => {
		if (!data?.pages?.length && isFetching) {
			return <ExamsShortViewSkeleton />;
		}

		if (hasLimitedAccess()) {
			return <ShortLimitedAccess />;
		}

		if (!data?.pages?.length && isFetched) {
			return (
				<div>
					<span>{t("defaultsText.emptyUsersQuestionList")}</span>
				</div>
			);
		}

		if (!data?.pages?.length) {
			return null;
		}

		const items = fetchedPages === 1 ? data.pages.slice(0, 2) : data.pages;

		return items.map((item: ForumComment) => {
			return (
				<UserQuestion
					comment={item}
					key={item.id}
				/>
			);
		});
	};

	const renderActions = () => {
		return (
			<TextButton
				className="user_questions_text_field-question-button"
				onClick={() => setIsModalOpen(true)}
			>
				<span className="size-16-light text-black">
					{t("news.buttons.addQuestion")}
				</span>
				<div className="menu-icon-button square-32">
					<PlusIcon className="icon-normal" />
				</div>
			</TextButton>
		);
	};

	const renderSearch = () => {
		return (
			<div className="flex items-start gap-12">
				{isMobile() && renderActions()}
				<IconTextField
					tooltip=""
					isLoading={isFetching}
					onSearch={setSearch}
					onOpenClassName="user_questions_text_field-container"
					textFieldClassName="user_questions_text_field"
					placeholder={t("news.searchPlaceholder")}
				/>
			</div>
		);
	};

	const renderMoreButton = () => {
		return (
			<TextButton
				className="primary-text-btn"
				endIcon={<ArrowDownIcon className="icon-normal" />}
				onClick={() => {
					if (isFirstFetch) {
						setFirstFetch(false);
					}
					fetchNextPage();
				}}
				isLoading={isFetching}
			>
				{t("commands.seeMore")}
			</TextButton>
		);
	};

	const createActionPortal = () => {
		if (!actionContainer || isMobile()) {
			return null;
		}

		return createPortal(
			renderActions(),
			document.getElementById(ActionContainerId)!,
		);
	};

	const createSearchPortal = () => {
		if (!searchContainer) {
			return null;
		}

		return createPortal(
			renderSearch(),
			document.getElementById(SearchContainerId)!,
		);
	};

	return (
		<div className="flex flex-col gap-12">
			{createActionPortal()}
			{createSearchPortal()}
			{isModalOpen && (
				<AddQuestionModal
					onClose={() => setIsModalOpen(false)}
					options={{
						search,
					}}
				/>
			)}
			<div
				className={cx("flex flex-col gap-12", {
					"last_comment--container": !hasNextPage,
				})}
			>
				{renderContent()}
			</div>
			{hasNextPage && (
				<div className="flex-center-row">{renderMoreButton()}</div>
			)}
		</div>
	);
}
